import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Link2,
  Loader2,
  RefreshCw,
  Sparkles,
  Upload,
  Download,
  FileSpreadsheet,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import apiService from '../services/api';
import {
  buildCollisionMap,
  buildDecisionPayload,
  canCommitReview,
  filterDraftForCommit,
  productKey,
  summarizeReview,
} from '../utils/importReview';
import { describeImportJobFailure } from '../utils/importJobFailure';
import { resolveMerchantOperationalScope } from '../utils/assistanceSession';

const SOURCE_OPTIONS = [
  { value: 'IMAGE', label: 'Image (JPG, PNG, WebP)' },
  { value: 'PDF', label: 'Document PDF' },
  { value: 'HTML', label: 'Fichier Document (HTML, Excel, CSV)' },
  { value: 'URL', label: 'Lien URL' },
];

const SOURCE_LABELS = {
  IMAGE: 'Image',
  PDF: 'PDF',
  HTML: 'Document HTML',
  URL: 'URL',
  SPREADSHEET: 'Tableur',
};

const JOB_STATUS_LABEL = {
  PENDING: 'En attente',
  PROCESSING: 'Analyse en cours',
  READY: 'Prêt à valider',
  FAILED: 'Échec',
  IMPORTED: 'Importé',
};

/** Aligné sur backend IMPORT_MAX_UPLOAD_BYTES (défaut 10 Mo) */
const MAX_UPLOAD_MB = Number(import.meta.env.VITE_IMPORT_MAX_UPLOAD_MB || 10);

function formatEuro(valueInCents) {
  if (valueInCents === null || valueInCents === undefined) return '';
  return (valueInCents / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseEuroInput(value) {
  if (typeof value !== 'string') return 0;
  const normalized = value.replace(',', '.').replace(/[^\d.]/g, '');
  const num = parseFloat(normalized);
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Miroir léger de backend/utils/normalizeForMatch (match lib import). */
function normalizeForMatch(input) {
  if (input == null) return '';
  if (typeof input !== 'string') {
    if (typeof input === 'number' || typeof input === 'boolean') input = String(input);
    else return '';
  }
  return input
    .trim()
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * M4.1 — tags draft → candidats caractéristiques.
 * Déjà en lib commerçant → selected true ; nouvelles → false.
 */
async function enrichDraftCaracteristiquesImport(draft, commercantId) {
  if (!draft?.categories || !commercantId) return draft;
  const copy = deepClone(draft);

  const uniqueTags = [];
  const seen = new Set();
  for (const cat of copy.categories || []) {
    for (const product of cat.products || []) {
      for (const tag of product.tags || []) {
        const label = String(tag || '').trim();
        if (!label) continue;
        const slug = normalizeForMatch(label);
        if (!slug || seen.has(slug)) continue;
        seen.add(slug);
        uniqueTags.push(label);
      }
    }
  }

  const inLib = new Set();
  try {
    const bulk = await apiService.listCommercantCaracteristiques({
      commercantId,
      q: '',
      limit: 50,
    });
    for (const item of bulk?.items || []) {
      const slug = normalizeForMatch(item.label);
      if (slug) inLib.add(slug);
    }
  } catch {
    // Preview sans lib : tout reste décoché (nouvelles).
  }

  for (const tag of uniqueTags) {
    const slug = normalizeForMatch(tag);
    if (inLib.has(slug)) continue;
    try {
      const hit = await apiService.listCommercantCaracteristiques({
        commercantId,
        q: tag,
        limit: 8,
      });
      if ((hit?.items || []).some((i) => normalizeForMatch(i.label) === slug)) {
        inLib.add(slug);
      }
    } catch {
      /* ignore */
    }
  }

  for (const cat of copy.categories || []) {
    for (const product of cat.products || []) {
      if (Array.isArray(product.caracteristiquesImport) && product.caracteristiquesImport.length) {
        continue;
      }
      const tags = Array.isArray(product.tags) ? product.tags : [];
      product.caracteristiquesImport = tags
        .map((tag) => String(tag || '').trim())
        .filter(Boolean)
        .slice(0, 12)
        .map((label) => ({
          label,
          selected: inLib.has(normalizeForMatch(label)),
          inLib: inLib.has(normalizeForMatch(label)),
        }));
    }
  }

  return copy;
}

const MENU_TEMPLATE_URL = '/templates/modele-import-menu-clickorico.xlsx';

const ImportIA = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCommercantId = useMemo(() => searchParams.get('commercantId') || '', [searchParams]);

  const [commercants, setCommercants] = useState([]);
  const [commercantId, setCommercantId] = useState(initialCommercantId);
  const [sourceType, setSourceType] = useState('IMAGE');
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [jobId, setJobId] = useState('');
  const [job, setJob] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editableDraft, setEditableDraft] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [commitSummary, setCommitSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [excludedCategories, setExcludedCategories] = useState({});
  const [excludedProducts, setExcludedProducts] = useState({});
  const [showCommitReview, setShowCommitReview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [detectedSourceType, setDetectedSourceType] = useState('AUTO');
  const [resolvedSourceType, setResolvedSourceType] = useState(null);
  const [resolvedMimeType, setResolvedMimeType] = useState(null);
  const caracEnrichDoneRef = useRef(false);

  useEffect(() => {
    const fetchCommercants = async () => {
      try {
        // Récupérer l'utilisateur actuel pour vérifier son rôle
        const getCurrentUser = () => {
          try {
            const userData = localStorage.getItem('admin_user');
            if (userData) {
              return JSON.parse(userData);
            }
          } catch (error) {
            console.error('Erreur parsing user data:', error);
          }
          return null;
        };

        const currentUser = getCurrentUser();
        const scope = resolveMerchantOperationalScope(currentUser);
        const isCommercant = scope.scopedToMerchant;
        const userCommercantId = scope.commercantId;

        if (isCommercant && userCommercantId) {
          // Pour un commerçant / assistance, charger uniquement sa boutique
          const commercant = await apiService.getCommercant(userCommercantId);
          setCommercants(commercant ? [commercant] : []);
          // Pré-sélectionner sa boutique
          if (commercant && !initialCommercantId) {
            setCommercantId(commercant.id);
          }
        } else {
          // Pour SUPER_ADMIN/ADMIN, charger toutes les boutiques
          const data = await apiService.getCommercants();
          // L'API retourne { data: [...], pagination: {...} }
          const commercantsArray = Array.isArray(data) 
            ? data 
            : (data?.data || data?.commercants || []);
          setCommercants(commercantsArray);
        }
      } catch (err) {
        console.error('Erreur chargement commerçants', err);
        setError(err.message || 'Erreur lors du chargement des commerçants');
      }
    };
    fetchCommercants();
  }, [initialCommercantId]);

  useEffect(() => {
    if (!jobId) return;
    let isActive = true;
    setPolling(true);

    const fetchJob = async () => {
      try {
        const data = await apiService.getMenuImportJob(jobId);
        if (!isActive) return;
        setJob(data);
        if (data?.meta) setMeta(data.meta);
        if (data?.status === 'READY') {
          setDraft(data.draftJson);
          // Ne pas écraser les éditions locales au poll (cases à cocher M4.1).
          setEditableDraft((prev) => prev || deepClone(data.draftJson));
          setPolling(false);
        } else if (data?.status === 'FAILED' || data?.status === 'IMPORTED') {
          setPolling(false);
        }
      } catch (err) {
        console.error('Erreur fetch job', err);
        setError(err.message || 'Erreur lors du suivi du job');
        setPolling(false);
      }
    };

    fetchJob();
    const interval = setInterval(fetchJob, 2000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [jobId]);

  useEffect(() => {
    caracEnrichDoneRef.current = false;
  }, [jobId]);

  // M4.1 — enrichir tags → caractéristiquesImport (une fois par job READY).
  useEffect(() => {
    if (job?.status !== 'READY' || !editableDraft || caracEnrichDoneRef.current) return;
    const needsEnrich = (editableDraft.categories || []).some((cat) =>
      (cat.products || []).some(
        (p) =>
          Array.isArray(p.tags) &&
          p.tags.length > 0 &&
          !Array.isArray(p.caracteristiquesImport),
      ),
    );
    if (!needsEnrich) {
      caracEnrichDoneRef.current = true;
      return;
    }
    let active = true;
    const cid = job.commercantId || commercantId;
    caracEnrichDoneRef.current = true; // anti double-fetch pendant l'async
    enrichDraftCaracteristiquesImport(editableDraft, cid).then((enriched) => {
      if (!active) return;
      setEditableDraft(enriched);
    });
    return () => {
      active = false;
    };
  }, [job?.status, job?.commercantId, jobId, commercantId, editableDraft]);

  useEffect(() => {
    if (!jobId || job?.status !== 'READY') return;
    let active = true;
    apiService
      .getMenuImportPreview(jobId)
      .then((data) => {
        if (active) setPreview(data);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Impossible de détecter les collisions catalogue');
      });
    return () => {
      active = false;
    };
  }, [jobId, job?.status]);

  const collisionMap = useMemo(() => buildCollisionMap(preview), [preview]);
  const filteredDraft = useMemo(
    () => filterDraftForCommit(editableDraft, excludedCategories, excludedProducts),
    [editableDraft, excludedCategories, excludedProducts],
  );
  const reviewSummary = useMemo(
    () =>
      summarizeReview(
        editableDraft,
        preview,
        decisions,
        excludedCategories,
        excludedProducts,
      ),
    [editableDraft, preview, decisions, excludedCategories, excludedProducts],
  );
  const reviewCanCommit = Boolean(preview) && canCommitReview(reviewSummary, filteredDraft);

  const resetFlow = () => {
    setFile(null);
    setUrl('');
    setJobId('');
    setJob(null);
    setDraft(null);
    setEditableDraft(null);
    setMeta(null);
    setCommitSummary(null);
    setPreview(null);
    setDecisions({});
    setExcludedCategories({});
    setExcludedProducts({});
    setShowCommitReview(false);
    setError('');
    setDetectedSourceType('AUTO');
    setResolvedSourceType(null);
    setResolvedMimeType(null);
    caracEnrichDoneRef.current = false;
  };

  const handleStartImport = async () => {
    try {
      setError('');
      setPreview(null);
      setDecisions({});
      setExcludedCategories({});
      setExcludedProducts({});
      setShowCommitReview(false);
      setResolvedSourceType(null);
      setResolvedMimeType(null);
      if (!commercantId) {
        setError('Veuillez sélectionner un commerçant.');
        return;
      }
      if (sourceType === 'URL') {
        if (!url) {
          setError('Veuillez saisir une URL à analyser.');
          return;
        }
        setLoading(true);
        const response = await apiService.createMenuImportJob({
          commercantId,
          sourceType,
          url,
        });
        setJobId(response.jobId);
        setJob({ status: 'PENDING' });
      } else {
        if (!file) {
          setError('Veuillez sélectionner un fichier.');
          return;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('commercantId', commercantId);
        formData.append('sourceType', sourceType);
        if (detectedSourceType && detectedSourceType !== 'AUTO') {
          formData.append('sourceTypeHint', detectedSourceType);
        }
        setLoading(true);
        const response = await apiService.uploadMenuImportFile(formData);
        setJobId(response.jobId);
        setJob({ status: 'PENDING' });
        setResolvedSourceType(response?.sourceTypeResolved || null);
        setResolvedMimeType(response?.mimeType || null);
      }
    } catch (err) {
      console.error('Erreur création job', err);
      setError(err.message || 'Impossible de démarrer l’import');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!jobId || !editableDraft) return;
    try {
      setLoading(true);
      await apiService.updateMenuImportDraft(jobId, editableDraft);
      setDraft(deepClone(editableDraft));
      const nextPreview = await apiService.getMenuImportPreview(jobId);
      setPreview(nextPreview);
    } catch (err) {
      console.error('Erreur sauvegarde draft', err);
      setError(err.message || 'Impossible de sauvegarder le draft');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!jobId || !editableDraft) return;
    try {
      setLoading(true);
      setError('');

      // Recalcule les collisions côté serveur à partir du draft édité avant
      // toute écriture catalogue, afin d'éviter toute divergence front/backend.
      await apiService.updateMenuImportDraft(jobId, editableDraft);
      const latestPreview = await apiService.getMenuImportPreview(jobId);
      setPreview(latestPreview);
      const latestSummary = summarizeReview(
        editableDraft,
        latestPreview,
        decisions,
        excludedCategories,
        excludedProducts,
      );
      if (latestSummary.undecided > 0) {
        setShowCommitReview(false);
        setError(
          `${latestSummary.undecided} collision(s) nécessitent une décision explicite avant validation.`,
        );
        return;
      }

      const draftToCommit = filterDraftForCommit(
        editableDraft,
        excludedCategories,
        excludedProducts,
      );
      if (!draftToCommit?.categories?.length) {
        setShowCommitReview(false);
        setError('Aucun produit inclus dans cet import.');
        return;
      }
      const decisionPayload = buildDecisionPayload(
        latestPreview,
        decisions,
        excludedCategories,
        excludedProducts,
        editableDraft,
      );

      await apiService.updateMenuImportDraft(jobId, draftToCommit);
      const result = await apiService.commitMenuImportJob(
        jobId,
        'IA2_PRODUCT_DECISIONS',
        decisionPayload,
      );
      setCommitSummary(result.summary);
      setJob((prev) => ({ ...(prev || {}), status: 'IMPORTED' }));
      setShowCommitReview(false);
    } catch (err) {
      console.error('Erreur commit', err);
      const detail =
        (typeof err?.data?.detail === 'string' && err.data.detail.trim()) ||
        (typeof err?.response?.data?.detail === 'string' && err.response.data.detail.trim()) ||
        '';
      const base = detail || err.message || 'Impossible de valider l’import';
      const rolledBackHint = /aucun produit n.?a été importé/i.test(base)
        ? base
        : `${base}\n\nAucun produit n’a été importé (transaction annulée).`;
      setError(rolledBackHint);
      setShowCommitReview(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategoryExcluded = (categoryIndex) => {
    setExcludedCategories((prev) => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex],
    }));
  };

  const toggleProductExcluded = (categoryIndex, productIndex) => {
    const key = productKey(categoryIndex, productIndex);
    setExcludedProducts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setCollisionDecision = (key, decision) => {
    setDecisions((prev) => ({ ...prev, [key]: decision }));
  };

  const decideAllCollisions = (decision) => {
    const next = { ...decisions };
    (preview?.collisions || []).forEach((collision) => {
      if (
        !excludedCategories[collision.categoryIndex] &&
        !excludedProducts[collision.productKey]
      ) {
        next[collision.productKey] = decision;
      }
    });
    setDecisions(next);
  };

  const updateCategoryField = (index, field, value) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      copy.categories[index][field] = field === 'position' ? Number(value) : value;
      return copy;
    });
  };

  const updateProductField = (catIndex, prodIndex, field, value) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      copy.categories[catIndex].products[prodIndex][field] = value;
      return copy;
    });
    if (field === 'name') {
      const key = productKey(catIndex, prodIndex);
      setDecisions((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const toggleCaracteristiqueImport = (catIndex, prodIndex, caracIndex) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      const list = copy.categories[catIndex].products[prodIndex].caracteristiquesImport;
      if (!Array.isArray(list) || !list[caracIndex]) return prev;
      list[caracIndex].selected = !list[caracIndex].selected;
      return copy;
    });
  };

  const setAllCaracteristiquesImport = (catIndex, prodIndex, selected) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      const list = copy.categories[catIndex].products[prodIndex].caracteristiquesImport;
      if (!Array.isArray(list) || !list.length) return prev;
      list.forEach((item) => {
        item.selected = Boolean(selected);
      });
      return copy;
    });
  };

  const updateVariantField = (catIndex, prodIndex, variantIndex, field, value) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      if (field === 'priceCents') {
        copy.categories[catIndex].products[prodIndex].variants[variantIndex][field] = parseEuroInput(value);
      } else {
        copy.categories[catIndex].products[prodIndex].variants[variantIndex][field] = value;
      }
      return copy;
    });
  };

  const updateOptionGroupField = (catIndex, prodIndex, groupIndex, field, value) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      copy.categories[catIndex].products[prodIndex].optionGroups[groupIndex][field] = value;
      return copy;
    });
  };

  const updateChoiceField = (catIndex, prodIndex, groupIndex, choiceIndex, field, value) => {
    setEditableDraft((prev) => {
      const copy = deepClone(prev);
      if (field === 'priceDeltaCents') {
        copy.categories[catIndex].products[prodIndex].optionGroups[groupIndex].choices[
          choiceIndex
        ][field] = parseEuroInput(value);
      } else {
        copy.categories[catIndex].products[prodIndex].optionGroups[groupIndex].choices[
          choiceIndex
        ][field] = value;
      }
      return copy;
    });
  };

  // S'assurer que commercants est toujours un tableau
  const commercantsArray = Array.isArray(commercants) ? commercants : [];
  const selectedCommercant = commercantsArray.find((c) => c.id === commercantId);

  // Cible d'écriture : job.commercantId (vérité du job) puis fallback select.
  const commitTargetCommercantId = job?.commercantId || commercantId || '';
  const commitTargetCommercant =
    commercantsArray.find((c) => c.id === commitTargetCommercantId) || null;
  const commitTargetDisplayName = commitTargetCommercant
    ? commitTargetCommercant.nom ||
      commitTargetCommercant.name ||
      commitTargetCommercant.raisonSociale ||
      null
    : null;
  const commitTargetLabel = commitTargetDisplayName
    ? commitTargetDisplayName
    : commitTargetCommercantId
      ? `Commerçant inconnu — ID : ${String(commitTargetCommercantId).slice(0, 8)}`
      : 'Commerçant inconnu';

  const acceptString = useMemo(() => {
    switch (sourceType) {
      case 'PDF':
        return 'application/pdf,.pdf';
      case 'HTML':
        return '.html,.htm,.xlsx,.xls,.csv,.txt,.json';
      case 'IMAGE':
      default:
        return 'image/png,image/jpeg,image/webp';
    }
  }, [sourceType]);

  const isFileAllowed = (candidate) => {
    if (!candidate) return false;
    const extension = candidate.name.split('.').pop()?.toLowerCase();
    if (sourceType === 'PDF') {
      return (
        candidate.type === 'application/pdf' ||
        extension === 'pdf'
      );
    }
    if (sourceType === 'IMAGE') {
      return (
        candidate.type.startsWith('image/') &&
        ['png', 'jpg', 'jpeg', 'webp'].includes(extension || '')
      );
    }
    // Documents (HTML / tableurs / csv / texte)
    const allowedExtensions = ['html', 'htm', 'xlsx', 'xls', 'csv', 'txt', 'json'];
    return allowedExtensions.includes(extension || '');
  };

  const inferSourceType = (candidate) => {
    if (!candidate) return 'AUTO';
    const extension = candidate.name.split('.').pop()?.toLowerCase() || '';
    const type = (candidate.type || '').toLowerCase();
    if (type === 'application/pdf' || extension === 'pdf') return 'PDF';
    if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp'].includes(extension)) return 'IMAGE';
    if (type === 'text/html' || ['html', 'htm'].includes(extension)) return 'HTML';
    if (
      type.includes('spreadsheet') ||
      type.includes('excel') ||
      ['xlsx', 'xls', 'csv'].includes(extension)
    ) {
      return 'SPREADSHEET';
    }
    return 'AUTO';
  };

  const handleFileSelection = (selectedFile) => {
    if (!selectedFile) return;
    if (!isFileAllowed(selectedFile)) {
      setError("Type de fichier non supporté pour cette source.");
      setDetectedSourceType('AUTO');
      return;
    }
    if (selectedFile.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (max ${MAX_UPLOAD_MB} Mo).`);
      setDetectedSourceType('AUTO');
      return;
    }
    setError('');
    setFile(selectedFile);
    const inferred = inferSourceType(selectedFile);
    setDetectedSourceType(inferred);
    setResolvedSourceType(null);
    setResolvedMimeType(null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (dragActive) setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      handleFileSelection(droppedFile);
    }
  };

  useEffect(() => {
    if (sourceType === 'URL') {
      setFile(null);
      setDetectedSourceType('AUTO');
      setResolvedSourceType(null);
      setResolvedMimeType(null);
    }
  }, [sourceType]);

  return (
    <div className="space-y-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </button>

      <div className="bg-white shadow-sm rounded-3xl p-8 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-primary-500" />
              <h1 className="text-2xl font-semibold text-gray-900">Assistant Import IA</h1>
            </div>
            <p className="mt-2 text-gray-500">
              Automatise l’intégration des cartes restaurants en quelques minutes.
            </p>
          </div>
          {jobId && (
            <div className="text-right">
              <p className="text-sm font-medium text-gray-500">Statut</p>
              <p
                className={`text-base font-semibold ${
                  job?.status === 'READY'
                    ? 'text-emerald-600'
                    : job?.status === 'FAILED'
                    ? 'text-rose-600'
                    : job?.status === 'IMPORTED'
                    ? 'text-blue-600'
                    : 'text-gray-700'
                }`}
              >
                {JOB_STATUS_LABEL[job?.status] || '—'}
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {job?.status === 'FAILED' && (() => {
          const failure = describeImportJobFailure(job, meta);
          if (!failure) return null;
          return (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50/70 px-5 py-4 text-rose-900">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-rose-600" />
                <div className="space-y-2 min-w-0">
                  <p className="text-sm font-semibold text-rose-700">{failure.statusLabel}</p>
                  <p className="text-sm leading-relaxed whitespace-pre-line">{failure.errorMessage}</p>
                  <p className="text-sm text-rose-800/90">{failure.explanation}</p>
                  {failure.rejectedNames.length > 0 && (
                    <p className="text-sm text-rose-800/90">
                      Éléments non retenus :{' '}
                      <span className="font-medium">{failure.rejectedNames.join(', ')}</span>
                      {(meta?.grounding?.rejectedNames || job?.meta?.grounding?.rejectedNames || [])
                        .length > failure.rejectedNames.length
                        ? '…'
                        : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        <section className="mt-8 space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1 space-y-4">
              <label className="block text-sm font-medium text-gray-700">Commerçant cible</label>
              {(() => {
                // Récupérer l'utilisateur actuel pour vérifier son rôle
                const getCurrentUser = () => {
                  try {
                    const userData = localStorage.getItem('admin_user');
                    if (userData) {
                      return JSON.parse(userData);
                    }
                  } catch (error) {
                    console.error('Erreur parsing user data:', error);
                  }
                  return null;
                };

                const currentUser = getCurrentUser();
                const scope = resolveMerchantOperationalScope(currentUser);
                const isCommercant = scope.scopedToMerchant;
                const userCommercantId = scope.commercantId;

                if (isCommercant && userCommercantId && commercantsArray.length === 1) {
                  // Pour un commerçant / assistance, afficher un champ désactivé avec sa boutique
                  return (
                    <input
                      type="text"
                      value={commercantsArray[0]?.nom || 'Mon commerce'}
                      disabled
                      className="w-full rounded-2xl border-gray-200 px-4 py-3 text-gray-900 bg-gray-100 cursor-not-allowed"
                    />
                  );
                } else {
                  // Pour SUPER_ADMIN/ADMIN, afficher le sélecteur
                  return (
                    <select
                      value={commercantId}
                      onChange={(e) => setCommercantId(e.target.value)}
                      disabled={Boolean(jobId)}
                      className={`w-full rounded-2xl border-gray-200 px-4 py-3 text-gray-900 focus:border-primary-400 focus:ring-primary-100 ${
                        jobId ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="">Sélectionnez un commerçant</option>
                      {commercantsArray.map((com) => (
                        <option key={com.id} value={com.id}>
                          {com.nom || com.name || com.raisonSociale || com.id}
                        </option>
                      ))}
                    </select>
                  );
                }
              })()}
              {selectedCommercant && (
                <p className="text-xs text-gray-500">
                  {selectedCommercant.adresse
                    ? `${selectedCommercant.adresse}, ${selectedCommercant.codePostal || ''} ${
                        selectedCommercant.ville || ''
                      }`
                    : ''}
                </p>
              )}
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Modèle Excel Clickorico</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Remplissez le tableur (nom, catégorie, sous-catégorie, prix, type produit,
                      unité…) puis importez-le en source « Document ». Type produit = PHYSIQUE ou
                      SERVICE ; les unités (KG, L…) vont dans la colonne Unité.
                    </p>
                  </div>
                </div>
                <a
                  href={MENU_TEMPLATE_URL}
                  download="modele-import-menu-clickorico.xlsx"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 shrink-0"
                >
                  <Download className="h-4 w-4" />
                  Télécharger le modèle
                </a>
              </div>

              <label className="block text-sm font-medium text-gray-700">Source à analyser</label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {SOURCE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSourceType(option.value)}
                    className={`flex h-24 flex-col items-center justify-center rounded-2xl border text-center transition ${
                      sourceType === option.value
                        ? 'border-primary-400 bg-primary-50 text-primary-600 shadow-sm'
                        : 'border-gray-200 hover:border-primary-200 hover:bg-primary-50/40'
                    }`}
                  >
                    {option.value === 'URL' ? <Link2 className="mb-2 h-6 w-6" /> : <Upload className="mb-2 h-6 w-6" />}
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {sourceType === 'URL' ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6">
              <label className="block text-sm font-medium text-gray-700">URL du menu</label>
              <input
                type="url"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="mt-2 w-full rounded-xl border-gray-200 px-4 py-3 text-gray-900 focus:border-primary-400 focus:ring-primary-100"
              />
            </div>
          ) : (
            <div
              className={`rounded-2xl border border-dashed p-6 transition ${
                dragActive
                  ? 'border-primary-400 bg-primary-50/70'
                  : 'border-gray-300 bg-gray-50/60'
              }`}
              onDragEnter={handleDragOver}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label className="block text-sm font-medium text-gray-700">Fichier à analyser</label>
              <div className="mt-3 flex flex-col items-center justify-center gap-3 rounded-xl bg-white/70 py-10 text-center">
                <input
                  id="import-file"
                  type="file"
                  accept={acceptString}
                  onChange={(e) => handleFileSelection(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <label
                  htmlFor="import-file"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-100"
                >
                  <Upload className="h-4 w-4" />
                  {file ? 'Remplacer le fichier' : 'Choisir un fichier'}
                </label>
                <p className="text-xs text-gray-500">
                  Glissez-déposez votre fichier ici ou cliquez pour sélectionner.
                </p>
                {file && <p className="text-xs text-gray-600">{file.name}</p>}
                {detectedSourceType && detectedSourceType !== 'AUTO' && (
                  <p className="text-xs text-gray-500">
                    Détection locale : {SOURCE_LABELS[detectedSourceType] || detectedSourceType}
                  </p>
                )}
                {resolvedSourceType && (
                  <p className="text-xs text-emerald-600">
                    Type confirmé par l’analyse : {SOURCE_LABELS[resolvedSourceType] || resolvedSourceType}
                    {resolvedMimeType ? ` • ${resolvedMimeType}` : ''}
                  </p>
                )}
                <p className="text-xs text-gray-400">Taille max {MAX_UPLOAD_MB} Mo</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleStartImport}
              disabled={loading || polling || !commercantId}
              className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Lancer l’analyse
            </button>
            {jobId && (
              <button
                type="button"
                onClick={resetFlow}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:border-gray-300"
              >
                <RefreshCw className="h-4 w-4" />
                Nouveau job
              </button>
            )}
            {polling && (
              <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyse en cours…
              </span>
            )}
          </div>
        </section>

        {meta?.warnings?.length ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <p className="font-medium">Attention</p>
            <ul className="mt-2 list-disc pl-5">
              {meta.warnings.map((warn, idx) => (
                <li key={idx}>{warn}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {editableDraft && (
        <div className="space-y-6">
          <div className="bg-white shadow-sm rounded-3xl border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Draft généré</h2>
                <p className="text-sm text-gray-500">
                  Ajustez librement les données avant import. Tous les montants sont exprimés en euros.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-300"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Sauvegarder et recalculer les collisions
                </button>
                <button
                  type="button"
                  onClick={() => setShowCommitReview(true)}
                  disabled={loading || !reviewCanCommit}
                  className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Valider l’import
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="text-xs font-medium uppercase text-gray-400">Job</label>
                <p className="text-sm text-gray-700">{jobId}</p>
              </div>
              <div>
                <label className="text-xs font-medium uppercase text-gray-400">Tokens</label>
                <p className="text-sm text-gray-700">
                  {meta?.tokensTotal || meta?.tokensPrompt || 0} tokens · coût estimé{' '}
                  {meta?.costEstimateEur ? `${meta.costEstimateEur.toFixed(2)} €` : '—'}
                </p>
              </div>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase text-blue-700">Catalogue protégé</p>
                <p className="mt-1 text-sm text-blue-800">
                  Les collisions sont détectées par le serveur et décidées produit par produit.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-800">
                    {reviewSummary.create} à créer
                  </span>
                  <span className="rounded-full bg-red-100 px-3 py-1 font-medium text-red-800">
                    {reviewSummary.replace} à remplacer
                  </span>
                  <span className="rounded-full bg-gray-200 px-3 py-1 font-medium text-gray-700">
                    {reviewSummary.ignore} ignoré(s)
                  </span>
                  <span className="rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-800">
                    {reviewSummary.excluded} exclu(s)
                  </span>
                  {reviewSummary.undecided > 0 && (
                    <span className="rounded-full bg-orange-600 px-3 py-1 font-semibold text-white">
                      {reviewSummary.undecided} décision(s) requise(s)
                    </span>
                  )}
                </div>
                {(preview?.collisions || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => decideAllCollisions('IGNORE')}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                    >
                      Ignorer toutes les collisions
                    </button>
                    <button
                      type="button"
                      onClick={() => decideAllCollisions('CREATE_NEW')}
                      className="rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700"
                    >
                      Tout créer en « (import) »
                    </button>
                  </div>
                )}
              </div>
              {!reviewCanCommit && reviewSummary.undecided > 0 && (
                <p className="mt-3 text-sm font-medium text-orange-700">
                  La validation reste bloquée tant que chaque collision incluse n’a pas de décision.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {editableDraft.categories.map((category, catIndex) => (
              <div
                key={catIndex}
                className={`rounded-3xl border shadow-sm ${
                  excludedCategories[catIndex]
                    ? 'border-gray-200 bg-gray-100 opacity-60'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                  <div className="flex-1">
                    <input
                      value={category.name}
                      onChange={(e) => updateCategoryField(catIndex, 'name', e.target.value)}
                      className="w-full rounded-lg border-none text-lg font-semibold text-gray-900 focus:ring-0"
                    />
                    <div className="mt-1 flex gap-4 text-xs text-gray-500">
                      <label className="flex items-center gap-1">
                        Position
                        <input
                          type="number"
                          value={category.position ?? 0}
                          onChange={(e) => updateCategoryField(catIndex, 'position', e.target.value)}
                          className="w-16 rounded-md border border-gray-200 px-2 py-0.5 text-gray-700 focus:border-primary-400 focus:ring-primary-100"
                        />
                      </label>
                      {category.externalRef && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                          Ref. {category.externalRef}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCategoryExcluded(catIndex)}
                    className={`ml-4 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                      excludedCategories[catIndex]
                        ? 'bg-white text-gray-700'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    {excludedCategories[catIndex] ? (
                      <><RotateCcw className="h-4 w-4" /> Réinclure la catégorie</>
                    ) : (
                      <><Trash2 className="h-4 w-4" /> Exclure la catégorie</>
                    )}
                  </button>
                </div>

                <div className="divide-y divide-gray-100">
                  {category.products.map((product, prodIndex) => (
                    <div
                      key={prodIndex}
                      className={`px-6 py-4 ${
                        excludedCategories[catIndex] ||
                        excludedProducts[productKey(catIndex, prodIndex)]
                          ? 'bg-gray-50 opacity-60'
                          : ''
                      }`}
                    >
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {collisionMap.has(productKey(catIndex, prodIndex)) ? (
                            <>
                              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800">
                                Existe déjà
                              </span>
                              <span className="text-xs text-gray-500">
                                Correspond à « {collisionMap.get(productKey(catIndex, prodIndex)).matchedProduitNom} »
                              </span>
                            </>
                          ) : (
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                              Nouveau produit
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleProductExcluded(catIndex, prodIndex)}
                          disabled={excludedCategories[catIndex]}
                          className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed"
                        >
                          {excludedProducts[productKey(catIndex, prodIndex)] ? (
                            <><RotateCcw className="h-3.5 w-3.5" /> Réinclure</>
                          ) : (
                            <><Trash2 className="h-3.5 w-3.5" /> Exclure ce produit</>
                          )}
                        </button>
                      </div>

                      {collisionMap.has(productKey(catIndex, prodIndex)) &&
                        !excludedCategories[catIndex] &&
                        !excludedProducts[productKey(catIndex, prodIndex)] && (
                          <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 p-4">
                            <p className="text-sm font-semibold text-orange-900">
                              Décision obligatoire pour cette collision
                            </p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-3">
                              {[
                                ['IGNORE', 'Ignorer', 'Conserver l’existant intact'],
                                ['CREATE_NEW', 'Créer une copie', 'Suffixe automatique « (import) »'],
                                ['REPLACE', 'Remplacer', 'Écrase ce produit et ses options'],
                              ].map(([value, label, help]) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() =>
                                    setCollisionDecision(productKey(catIndex, prodIndex), value)
                                  }
                                  className={`rounded-xl border p-3 text-left ${
                                    decisions[productKey(catIndex, prodIndex)] === value
                                      ? value === 'REPLACE'
                                        ? 'border-red-500 bg-red-100 ring-2 ring-red-200'
                                        : 'border-primary-500 bg-white ring-2 ring-primary-100'
                                      : 'border-orange-200 bg-white hover:border-orange-300'
                                  }`}
                                >
                                  <span className="block text-sm font-semibold text-gray-900">{label}</span>
                                  <span className="mt-1 block text-xs text-gray-600">{help}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="flex-1">
                          <input
                            value={product.name}
                            onChange={(e) => updateProductField(catIndex, prodIndex, 'name', e.target.value)}
                            className="w-full rounded-lg border-none text-base font-medium text-gray-900 focus:ring-0"
                          />
                          <textarea
                            value={product.description || ''}
                            onChange={(e) => updateProductField(catIndex, prodIndex, 'description', e.target.value)}
                            placeholder="Description (optionnelle)"
                            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 focus:border-primary-400 focus:ring-primary-100"
                          />
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <label className="text-xs font-medium text-gray-500" htmlFor={`type-${catIndex}-${prodIndex}`}>
                              Type catalogue
                            </label>
                            <select
                              id={`type-${catIndex}-${prodIndex}`}
                              value={product.typeProduit || 'PHYSIQUE'}
                              onChange={(e) => updateProductField(catIndex, prodIndex, 'typeProduit', e.target.value)}
                              title="Corrigeable sans relancer OCR / OpenAI — persisté au save / commit"
                              className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700"
                            >
                              <option value="PHYSIQUE">Produit (PHYSIQUE)</option>
                              <option value="SERVICE">Prestation (SERVICE)</option>
                            </select>
                            {product.caracteristiquesImport?.length ? (
                              <div className="mt-2 w-full">
                                {(() => {
                                  const list = product.caracteristiquesImport;
                                  const selectedCount = list.filter((c) => c.selected).length;
                                  const knownCount = list.filter((c) => c.inLib).length;
                                  const newCount = list.length - knownCount;
                                  return (
                                <>
                                <div className="mb-1 flex flex-wrap items-center gap-2">
                                  <p className="text-xs font-medium text-gray-500">
                                    Caractéristiques à importer
                                    <span className="ml-1 font-normal text-gray-400">
                                      {selectedCount}/{list.length} sélectionnée
                                      {selectedCount > 1 ? 's' : ''}
                                      {' · '}
                                      {knownCount} déjà en bibliothèque
                                      {' · '}
                                      {newCount} nouvelle{newCount > 1 ? 's' : ''}
                                    </span>
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAllCaracteristiquesImport(catIndex, prodIndex, true)
                                    }
                                    className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700 hover:bg-primary-100"
                                  >
                                    Tout sélectionner
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAllCaracteristiquesImport(catIndex, prodIndex, false)
                                    }
                                    className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
                                  >
                                    Tout désélectionner
                                  </button>
                                </div>
                                <p className="mb-1.5 text-[11px] text-gray-400">
                                  Déjà en bibliothèque = cochées. Nouvelles = décochées. Seules les
                                  cases cochées seront ajoutées au catalogue.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {list.map((carac, caracIndex) => (
                                    <label
                                      key={`${carac.label}-${caracIndex}`}
                                      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                                        carac.selected
                                          ? 'border-primary-400 bg-primary-50 text-primary-800'
                                          : 'border-gray-200 bg-white text-gray-600'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={Boolean(carac.selected)}
                                        onChange={() =>
                                          toggleCaracteristiqueImport(catIndex, prodIndex, caracIndex)
                                        }
                                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                      />
                                      <span>{carac.label}</span>
                                      {carac.inLib ? (
                                        <span className="text-[10px] text-gray-400">
                                          déjà en bibliothèque
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-gray-400">nouvelle</span>
                                      )}
                                    </label>
                                  ))}
                                </div>
                                </>
                                  );
                                })()}
                              </div>
                            ) : product.tags?.length ? (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                {product.tags.map((tag) => (
                                  <span key={tag} className="rounded-full bg-gray-100 px-2 py-0.5">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {product.externalRef && (
                              <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-600">
                                Ref. {product.externalRef}
                              </span>
                            )}
                          </div>
                        </div>
                        {product.variants?.length > 0 && (
                          <div className="w-full md:w-56">
                            <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-3">
                              <p className="text-xs font-medium uppercase text-gray-500">Variantes</p>
                              <div className="mt-2 space-y-2">
                                {product.variants.map((variant, variantIndex) => (
                                  <div key={variantIndex} className="rounded-xl bg-white px-3 py-2 shadow-inner">
                                    <input
                                      value={variant.name || ''}
                                      onChange={(e) =>
                                        updateVariantField(catIndex, prodIndex, variantIndex, 'name', e.target.value)
                                      }
                                      placeholder="Nom"
                                      className="w-full border-none text-sm font-medium text-gray-700 focus:ring-0"
                                    />
                                    <input
                                      value={formatEuro(variant.priceCents)}
                                      onChange={(e) =>
                                        updateVariantField(
                                          catIndex,
                                          prodIndex,
                                          variantIndex,
                                          'priceCents',
                                          e.target.value,
                                        )
                                      }
                                      className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1 text-right text-sm text-gray-700 focus:border-primary-400 focus:ring-primary-100"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {product.optionGroups?.length ? (
                        <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                          <p className="text-xs font-semibold uppercase text-gray-500">Options & suppléments</p>
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {product.optionGroups.map((group, groupIndex) => (
                              <div key={groupIndex} className="rounded-2xl border border-gray-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <input
                                    value={group.name}
                                    onChange={(e) =>
                                      updateOptionGroupField(catIndex, prodIndex, groupIndex, 'name', e.target.value)
                                    }
                                    className="flex-1 border-none text-sm font-semibold text-gray-800 focus:ring-0"
                                  />
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                                    {group.type === 'SINGLE' ? 'Choix unique' : 'Multi'}
                                  </span>
                                </div>
                                <div className="mt-3 space-y-2">
                                  {group.choices.map((choice, choiceIndex) => (
                                    <div key={choiceIndex} className="rounded-xl bg-gray-50 px-3 py-2">
                                      <input
                                        value={choice.name}
                                        onChange={(e) =>
                                          updateChoiceField(
                                            catIndex,
                                            prodIndex,
                                            groupIndex,
                                            choiceIndex,
                                            'name',
                                            e.target.value,
                                          )
                                        }
                                        className="w-full border-none text-sm text-gray-700 focus:ring-0"
                                      />
                                      <input
                                        value={formatEuro(choice.priceDeltaCents)}
                                        onChange={(e) =>
                                          updateChoiceField(
                                            catIndex,
                                            prodIndex,
                                            groupIndex,
                                            choiceIndex,
                                            'priceDeltaCents',
                                            e.target.value,
                                          )
                                        }
                                        className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1 text-right text-xs text-gray-600 focus:border-primary-400 focus:ring-primary-100"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCommitReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900">Confirmer l’écriture du catalogue</h2>
            <p className="mt-2 text-sm text-gray-600">
              Vérifie ce récapitulatif : seules les actions ci-dessous seront exécutées.
            </p>
            <div className="mt-5 rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Catalogue cible
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{commitTargetLabel}</p>
              <p className="mt-2 text-sm text-amber-950">
                Les créations, modifications et remplacements seront appliqués uniquement au
                catalogue de ce commerçant.
              </p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-2xl bg-emerald-50 p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{reviewSummary.create}</p>
                <p className="text-xs text-emerald-800">Créations</p>
              </div>
              <div className="rounded-2xl bg-red-50 p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{reviewSummary.replace}</p>
                <p className="text-xs text-red-800">Remplacements</p>
              </div>
              <div className="rounded-2xl bg-gray-100 p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{reviewSummary.ignore}</p>
                <p className="text-xs text-gray-700">Ignorés</p>
              </div>
              <div className="rounded-2xl bg-amber-50 p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{reviewSummary.excluded}</p>
                <p className="text-xs text-amber-800">Exclus</p>
              </div>
            </div>
            {reviewSummary.replace > 0 && (
              <div className="mt-4 flex gap-3 rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-900">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <p>
                  {reviewSummary.replace} produit(s) existant(s) seront remplacés, y compris leurs options.
                  Cette action est volontaire et explicite.
                </p>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCommitReview(false)}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700"
              >
                Revenir à la review
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={loading || !reviewCanCommit}
                className="rounded-xl bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Validation…' : `Importer chez ${commitTargetLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {commitSummary && (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50/80 p-6">
          <div className="flex items-center gap-3 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            <div>
              <p className="font-semibold">Import validé</p>
              <p className="text-sm">
                Résultat : {commitSummary.productsCreated ?? commitSummary.created ?? 0} créations ·{' '}
                {commitSummary.productsUpdated ?? commitSummary.updated ?? 0} remplacements ·{' '}
                {commitSummary.productsIgnored ?? 0} ignorés
              </p>
            </div>
          </div>
          {commitSummary.warnings?.length ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700">
              <p className="text-sm font-semibold">Points à vérifier :</p>
              <ul className="mt-2 list-disc pl-5 text-sm">
                {commitSummary.warnings.map((warn, idx) => (
                  <li key={idx}>{warn}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ImportIA;

